import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaShieldAlt, FaGamepad, FaChevronDown, FaKey, FaUser, FaHashtag, FaSyncAlt, FaCheckCircle, FaInfoCircle, FaTimes } from 'react-icons/fa';

const ACCENT_COLOR = '#ffb703'; // Match the platform's gold brand color

const ProvablyFair = () => {
    const [activeTab, setActiveTab] = useState('Fairness');
    const [clientSeed, setClientSeed] = useState('');
    const [nonce, setNonce] = useState(0);
    const [serverSeedHash, setServerSeedHash] = useState('');
    const [betId, setBetId] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [wagerData, setWagerData] = useState(null);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const location = useLocation();

    useEffect(() => {
        // Fetch current fairness settings
        fetch('/api/user/fairness')
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setClientSeed(data.clientSeed);
                    setNonce(data.nonce);
                    setServerSeedHash(data.serverSeedHash);
                }
            })
            .catch(err => console.error('Error fetching fairness:', err));
    }, []);

    const handleRandomize = () => {
        const newSeed = Math.random().toString(36).substring(2, 12);
        setClientSeed(newSeed);
    };

    const handleSave = async () => {
        if (!clientSeed) return;
        setIsSaving(true);
        try {
            const response = await fetch('/api/user/update-client-seed', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clientSeed })
            });
            const data = await response.json();
            if (data.success) {
                setNonce(0);
                alert('Client seed saved successfully!');
            } else {
                alert(data.error || 'Failed to save client seed');
            }
        } catch (err) {
            console.error('Save error:', err);
            alert('Failed to save client seed');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSearch = async () => {
        if (!betId) return;
        setIsVerifying(true);
        setError('');
        setWagerData(null);
        
        try {
            const response = await fetch(`/api/wager/verify/${betId}`);
            const data = await response.json();
            
            if (data.success) {
                setWagerData(data.verificationData);
            } else {
                setError(data.error || 'Bet not found');
            }
        } catch (err) {
            console.error('Search error:', err);
            setError('Failed to fetch bet details');
        } finally {
            setIsVerifying(false);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab) {
            // Map tab param to display name
            const tabMap = {
                'faq': 'FAQ',
                'aml': 'AML',
                'cookies': 'Cookie',
                'privacy': 'Privacy',
                'tos': 'TOS',
                'fairness': 'Fairness'
            };
            if (tabMap[tab.toLowerCase()]) {
                setActiveTab(tabMap[tab.toLowerCase()]);
            }
        }
    }, [location]);

    const blockStyle = {
        background: '#16191f',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '20px',
        border: '1px solid rgba(255, 255, 255, 0.05)'
    };

    const labelStyle = {
        color: 'white',
        fontSize: '20px',
        fontWeight: '900',
        marginBottom: '15px',
        display: 'block'
    };

    const textStyle = {
        color: 'rgba(255,255,255,0.4)',
        fontSize: '14px',
        lineHeight: '1.6',
        marginBottom: '20px',
        fontWeight: '500'
    };

    const inputStyle = {
        width: '100%',
        background: '#0d0f13',
        border: '1px solid rgba(255,255,255,0.05)',
        padding: '12px 16px',
        borderRadius: '8px',
        color: 'white',
        fontSize: '14px',
        fontFamily: 'monospace',
        outline: 'none'
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'Fairness':
                return (
                    <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
                        {/* LEFT COLUMN */}
                        <div style={{ flex: 1.5 }}>
                            <div style={blockStyle}>
                                <span style={labelStyle}>Client seed</span>
                                <p style={textStyle}>
                                    The Client Seed is a randomly generated string created by your browser. It is sent to the server when a PvE game starts, after the Server Hash is shared and the Server Seed is locked in. You can change the Client Seed however you like.
                                </p>
                                <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '8px' }}>Client seed</div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <div style={{ position: 'relative', flex: 1 }}>
                                        <input type="text" value={clientSeed} onChange={e => setClientSeed(e.target.value)} style={inputStyle} />
                                        <FaTimes size={10} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.2)', cursor: 'pointer' }} onClick={() => setClientSeed('')} />
                                    </div>
                                    <button onClick={handleRandomize} style={{ background: ACCENT_COLOR, color: '#000', border: 'none', padding: '0 20px', borderRadius: '8px', fontWeight: '900', fontSize: '13px', cursor: 'pointer' }}>Randomize</button>
                                    <button onClick={handleSave} disabled={isSaving} style={{ background: ACCENT_COLOR, color: '#000', border: 'none', padding: '0 20px', borderRadius: '8px', fontWeight: '900', fontSize: '13px', cursor: 'pointer' }}>
                                        {isSaving ? '...' : 'Save'}
                                    </button>
                                </div>
                            </div>

                            <div style={blockStyle}>
                                <span style={labelStyle}>What is Provably Fair?</span>
                                <p style={textStyle}>
                                    Provably Fair is a system that lets players check that the site runs games honestly and doesn't change the results. It uses cryptography and input from outside sources to create random results. After the game, players can confirm that the outcome was based on the original seed and inputs, proving the game was fair.
                                </p>
                            </div>

                            <div style={blockStyle}>
                                <span style={labelStyle}>Nonce</span>
                                <p style={textStyle}>
                                    The nonce is a number that increases with each new bet, ensuring a unique result every time without needing to regenerate the client or server seeds. Its use, combined with the SHA256 cryptographic function, allows new outcomes to be generated while maintaining the same client and server seed pair. This ensures fairness and continuity in generating random results.
                                </p>
                            </div>
                        </div>

                        {/* RIGHT COLUMN */}
                        <div style={{ flex: 1 }}>
                            <div style={{ ...blockStyle, position: 'sticky', top: '20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <span style={{ color: 'white', fontSize: '24px', fontWeight: '900' }}>Outcome verifier</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer' }}>
                                        <FaGamepad size={14} style={{ color: 'rgba(255,255,255,0.4)' }} />
                                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: '800' }}>Games</span>
                                        <FaChevronDown size={10} style={{ color: 'rgba(255,255,255,0.4)' }} />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '8px' }}>Bet ID</div>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <input 
                                            type="text" 
                                            placeholder="Enter bet ID" 
                                            value={betId} 
                                            onChange={e => setBetId(e.target.value)} 
                                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                            style={inputStyle} 
                                        />
                                        <button 
                                            style={{ background: ACCENT_COLOR, color: '#000', border: 'none', padding: '0 24px', borderRadius: '8px', fontWeight: '900', fontSize: '14px', cursor: 'pointer' }}
                                            onClick={handleSearch}
                                            disabled={isVerifying}
                                        >
                                            {isVerifying ? 'Searching...' : 'Search'}
                                        </button>
                                    </div>
                                    {error && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '8px', fontWeight: '700' }}>{error}</div>}
                                </div>

                                {wagerData && (
                                    <div style={{ background: '#0d0f13', borderRadius: '12px', padding: '20px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                                            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: '800' }}>Game</span>
                                            <span style={{ color: 'white', fontSize: '12px', fontWeight: '900', textTransform: 'capitalize' }}>{wagerData.gameType}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                                            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: '800' }}>Outcome</span>
                                            <span style={{ color: wagerData.outcome === 'win' ? '#4ade80' : '#ef4444', fontSize: '12px', fontWeight: '900', textTransform: 'uppercase' }}>{wagerData.outcome}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                                            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: '800' }}>Profit</span>
                                            <span style={{ color: wagerData.profit > 0 ? '#4ade80' : 'white', fontSize: '12px', fontWeight: '900' }}>{wagerData.profit.toFixed(2)}</span>
                                        </div>
                                        <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '8px' }}>Client Seed</div>
                                            <div style={{ background: '#16191f', padding: '10px', borderRadius: '6px', fontSize: '11px', color: '#fff', fontFamily: 'monospace', wordBreak: 'break-all' }}>{wagerData.clientSeed}</div>
                                        </div>
                                        <div style={{ marginTop: '12px' }}>
                                            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '8px' }}>Nonce</div>
                                            <div style={{ background: '#16191f', padding: '10px', borderRadius: '6px', fontSize: '11px', color: '#fff', fontFamily: 'monospace' }}>{wagerData.nonce}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            case 'FAQ':
                return (
                    <div style={{ maxWidth: '800px' }}>
                        <h2 style={{ color: 'white', fontSize: '32px', fontWeight: '900', marginBottom: '30px' }}>Frequently Asked Questions</h2>
                        {[
                            { q: "What is RustyCoin?", a: "RustyCoin is a premier Rust skin gambling platform offering Provably Fair games like Case Opening, Battles, Coinflip, and more." },
                            { q: "How do I deposit?", a: "You can deposit via various cryptocurrencies or Rust skins through our secure payment gateway." },
                            { q: "Are the games fair?", a: "Yes! All our games use a Provably Fair system, which means the outcome is predetermined before you even bet and can be verified by anyone." },
                            { q: "How long do withdrawals take?", a: "Crypto withdrawals are usually processed within minutes. Skin withdrawals depend on the Steam network but are typically instant." },
                            { q: "Deposit/withdraw", a: "You can easily manage your funds via the Cashier in the top right. We support a wide range of deposit and withdrawal methods." }
                        ].map((item, i) => (
                            <div key={i} style={blockStyle}>
                                <h3 style={{ color: ACCENT_COLOR, fontSize: '18px', fontWeight: '800', marginBottom: '12px' }}>{item.q}</h3>
                                <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6', fontSize: '14px' }}>{item.a}</p>
                            </div>
                        ))}
                    </div>
                );
            case 'TOS':
                return (
                    <div style={{ maxWidth: '800px' }}>
                        <h2 style={{ color: 'white', fontSize: '32px', fontWeight: '900', marginBottom: '30px' }}>Terms of Service</h2>
                        {[
                            { title: "1. Agreement to Terms", content: "By accessing or using RustyCoin, you agree to be bound by these Terms of Service. If you do not agree to all of these terms, do not use our services." },
                            { title: "2. Eligibility", content: "You must be at least 18 years of age (or the legal age of majority in your jurisdiction) to use our platform. By using RustyCoin, you represent and warrant that you meet this requirement." },
                            { title: "3. User Accounts", content: "You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account." },
                            { title: "4. Prohibited Activities", content: "You may not use our platform for any illegal purpose. Prohibited activities include but are not limited to using automated systems or bots, attempting to circumvent security, or engaging in money laundering." }
                        ].map((section, i) => (
                            <div key={i} style={blockStyle}>
                                <h3 style={{ color: ACCENT_COLOR, fontSize: '18px', fontWeight: '800', marginBottom: '12px' }}>{section.title}</h3>
                                <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6', fontSize: '14px' }}>{section.content}</p>
                            </div>
                        ))}
                    </div>
                );
            case 'Privacy':
                return (
                    <div style={{ maxWidth: '800px' }}>
                        <h2 style={{ color: 'white', fontSize: '32px', fontWeight: '900', marginBottom: '30px' }}>Privacy Policy</h2>
                        {[
                            { title: "1. Information We Collect", content: "We collect information that you provide to us directly when you create an account, such as your username, email address, and IP address." },
                            { title: "2. How We Use Information", content: "We use your information to provide and maintain our services, communicate with you, and detect fraudulent activities." },
                            { title: "3. Data Security", content: "We take reasonable measures to protect your information from loss, theft, misuse, unauthorized access, and disclosure." }
                        ].map((section, i) => (
                            <div key={i} style={blockStyle}>
                                <h3 style={{ color: ACCENT_COLOR, fontSize: '18px', fontWeight: '800', marginBottom: '12px' }}>{section.title}</h3>
                                <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6', fontSize: '14px' }}>{section.content}</p>
                            </div>
                        ))}
                    </div>
                );
            case 'AML':
                return (
                    <div style={{ maxWidth: '800px' }}>
                        <h2 style={{ color: 'white', fontSize: '32px', fontWeight: '900', marginBottom: '30px' }}>AML Policy</h2>
                        {[
                            { title: "1. Introduction", content: "RustyCoin is committed to the highest standards of Anti-Money Laundering (AML) and Counter-Terrorist Financing (CTF) procedures." },
                            { title: "2. Verification Procedures", content: "We may require users to provide valid identification and proof of address in certain circumstances, such as high-volume transactions." },
                            { title: "3. Monitoring of Activity", content: "All transactions are monitored for potential signs of money laundering. We reserve the right to report suspicious activity." }
                        ].map((section, i) => (
                            <div key={i} style={blockStyle}>
                                <h3 style={{ color: ACCENT_COLOR, fontSize: '18px', fontWeight: '800', marginBottom: '12px' }}>{section.title}</h3>
                                <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6', fontSize: '14px' }}>{section.content}</p>
                            </div>
                        ))}
                    </div>
                );
            case 'Cookie':
                return (
                    <div style={{ maxWidth: '800px' }}>
                        <h2 style={{ color: 'white', fontSize: '32px', fontWeight: '900', marginBottom: '30px' }}>Cookie Policy</h2>
                        {[
                            { title: "1. What Are Cookies", content: "Cookies are small text files stored on your device to make websites work more efficiently and provide information to site owners." },
                            { title: "2. How We Use Cookies", content: "We use cookies to keep you signed in, understand usage patterns, and ensure platform security." }
                        ].map((section, i) => (
                            <div key={i} style={blockStyle}>
                                <h3 style={{ color: ACCENT_COLOR, fontSize: '18px', fontWeight: '800', marginBottom: '12px' }}>{section.title}</h3>
                                <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6', fontSize: '14px' }}>{section.content}</p>
                            </div>
                        ))}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div style={{ minHeight: '100%', color: 'white', fontFamily: "'Montserrat', sans-serif", padding: '40px 20px' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                {/* TABS ROW */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '30px' }}>
                    {['FAQ', 'AML', 'Cookie', 'Privacy', 'TOS', 'Fairness'].map(tab => (
                        <div 
                            key={tab} 
                            onClick={() => setActiveTab(tab)}
                            style={{ 
                                padding: '10px 20px', 
                                borderRadius: '8px', 
                                background: tab === activeTab ? 'rgba(255, 183, 3, 0.15)' : 'rgba(255,255,255,0.05)', 
                                color: tab === activeTab ? ACCENT_COLOR : 'rgba(255,255,255,0.4)', 
                                fontSize: '14px', 
                                fontWeight: '800',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => { if(tab !== activeTab) e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={e => { if(tab !== activeTab) e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
                        >
                            {tab}
                        </div>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                    >
                        {renderContent()}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default ProvablyFair;
